import { ParameterTypes } from "api/borrower"
import { NumberSelector } from "components/forms/NumberSelector"
import { Sizes } from "components/ui/Loader"
import { FC, useEffect, useState } from "react"
import { BorrowerFunnelCardWarning } from "../pricing-funnels/BorrowerFunnelCardWarning"

interface BorrowerCommissionCardItemProps {
  title: string
  subtitle?: string
  value: number
  size?: Sizes.sm | Sizes.md
  onChange: React.Dispatch<React.SetStateAction<number>>
  parameters?: ConfigurableParameterDto
  disabledUnit?: boolean
  warningMessage?: string
}

interface ConfigurableParameterDto {
  type: ParameterTypes
  values?: Array<number>
  unit?: string
  warningMessage?: string
}

export const BorrowerCommissionCardItem: FC<BorrowerCommissionCardItemProps> = ({
  title,
  subtitle,
  value,
  size = Sizes.md,
  onChange,
  parameters,
  disabledUnit = false,
  warningMessage,
}) => {
  // State local pour gérer les ajustements automatiques
  const [localValue, setLocalValue] = useState(value);

  // Synchroniser le state local avec la prop value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Gestion des paramètres selon le type
  const getInputProps = () => {
    if (!parameters) return {};
    switch (parameters.type) {
      case ParameterTypes.NUMBER_1: // RANGE
        if (parameters.values && parameters.values.length >= 2) {
          return {
            min: Math.min(...parameters.values),
            max: Math.max(...parameters.values),
            unit: disabledUnit ? undefined : parameters.unit,
            step: 1
          };
        }
        return {};
      case ParameterTypes.NUMBER_2: // VALUES (liste de choix)
        return {
          allowedValues: parameters.values,
          unit: disabledUnit ? undefined : parameters.unit,
        };
      default:
        return {};
    }
  };

  // Fonction pour les changements manuels (déclenche le dispatch)
  const handleManualChange = (newValue: React.SetStateAction<number>) => {
    const resolvedValue = typeof newValue === 'function' ? newValue(localValue) : newValue;
    setLocalValue(resolvedValue);
    onChange(newValue);
  };

  // Ajustement automatique de la valeur si elle dépasse l'intervalle
  useEffect(() => {
    if (!parameters) return;

    let needsAdjustment = false;
    let adjustedValue = localValue;

    switch (parameters.type) {
      case ParameterTypes.NUMBER_1: // RANGE
        if (parameters.values && parameters.values.length >= 2) {
          const min = Math.min(...parameters.values);
          const max = Math.max(...parameters.values);
          if (localValue < min) {
            adjustedValue = min;
            needsAdjustment = true;
          } else if (localValue > max) {
            adjustedValue = max;
            needsAdjustment = true;
          }
        }
        break;
      case ParameterTypes.NUMBER_2: // VALUES (liste de choix)
        if (parameters.values && parameters.values.length > 0) {
          if (!parameters.values.includes(localValue)) {
            // Si la valeur n'est pas dans la liste, prendre la valeur la plus proche
            const closest = parameters.values.reduce((prev, curr) =>
              Math.abs(curr - localValue) < Math.abs(prev - localValue) ? curr : prev
            );
            adjustedValue = closest;
            needsAdjustment = true;
          }
        }
        break;
    }

    if (needsAdjustment) {
      // Ajuster seulement la valeur locale, sans déclencher onChange (pas de dispatch)
      setLocalValue(adjustedValue);
    }
  }, [parameters, localValue]);

  // Pour le type VALUES, on peut créer un select ou adapter le NumberSelector
  const renderInput = () => {
    const inputProps = getInputProps();
    return (
      <NumberSelector
        value={localValue}
        variant={size}
        onChange={handleManualChange}
        id={`selector-${title.replace(/\s+/g, '-').toLowerCase()}`}
        values={parameters?.type === ParameterTypes.NUMBER_2 ? parameters.values : undefined}
        {...inputProps}
      />
    );
  };

  return (
    <div className={size === Sizes.sm ? "flex flex-col items-start" : "flex items-center"}>
      <div className="flex flex-col w-full">
        <div className="flex items-center gap-1">
          <p className={size === Sizes.sm ? "text-xs" : "text-sm"}>
            {title}
          </p>
          {warningMessage && (
            <BorrowerFunnelCardWarning warningMessages={[warningMessage]} />
          )}
        </div>
        {subtitle && (
          <p className="text-xs font-light">
            {subtitle}
          </p>
        )}
      </div>
      <div className={size === Sizes.sm ? 'w-24' : 'ml-2'}>
        {renderInput()}
      </div>
    </div>
  )
}
